/* Spielerliste als CSV oder PDF exportieren.
 *
 * Alles läuft im Browser: Die Datei entsteht aus den schon geladenen Spielern und wird direkt
 * heruntergeladen. Nichts wird gespeichert, kein Protokolleintrag, keine Benachrichtigung.
 * Einzige Abfrage: die Transferhistorie der transferierten Spieler (aufnehmender Verein, Datum) —
 * dieselbe Tabelle und dieselbe Datenbankregel wie im Reiter "Transferhistorie".
 *
 * Wer exportieren darf und welche Umfänge, steht in domain/playerListExport.js (canExportPlayerList,
 * exportScopesFor; staff nur Kader und Auswahl, nie Transfers). Die Prüfung
 * steht hier IM Ablauf, nicht nur beim Knopf: Wer die Funktion trotzdem aufruft, bekommt 'forbidden'.
 * Gäste lesen ohnehin nur die öffentliche Ansicht ohne Geburtsdatum; sie stehen nicht auf der Liste.
 */

import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePlayers } from './usePlayers.js';
import { useAuth } from './useAuth.js';
import { listTransfers } from '../supabase/transfers.js';
import { useFootballClubs } from './useFootballClubs.js';
import { useSelection } from './useSelection.js';
import {
  canExportPlayerList, exportScopesFor, buildExportTable, buildCsv, exportFilename, selectPlayers,
  RUN_SCOPES, EXPORT_FORMATS, SELECTION_SCOPE, sanitizeListTitle,
} from '../domain/playerListExport.js';
import { exportLabels } from '../export/exportLabels.js';
import { downloadFile as download } from '../export/download.js';

/** Logo laden; fehlt es, wird das PDF ohne gezeichnet (das Logo ist Schmuck, kein Inhalt). */
async function loadImage(url) {
  try {
    const res = await fetch(url);
    if (!res.ok) return undefined;
    return new Uint8Array(await res.arrayBuffer());
  } catch {
    return undefined;
  }
}

export function usePlayerListExport() {
  const { t } = useI18n();
  const { players } = usePlayers();
  const { role } = useAuth();
  const clubRegister = useFootballClubs();
  const selection = useSelection();

  const allowed = computed(() => canExportPlayerList(role.value));
  /** Umfänge, die diese Rolle wählen darf (domain/playerListExport.js exportScopesFor) */
  const scopes = computed(() => exportScopesFor(role.value));
  const busy = ref(false);
  /** Schlüssel unter playerExport.* (failed, transferFailed, empty) oder null */
  const error = ref(null);

  /** Anzahl Spieler je Umfang — für die Vorschau im Dialog. */
  function countFor(scope) {
    /* Nicht erlaubter Umfang: nichts zählen, nichts auswählen */
    if (!exportScopesFor(role.value).includes(scope)) return 0;
    /* Nicht erlaubter Umfang: nichts zählen, nichts auswählen */
    if (!exportScopesFor(role.value).includes(scope)) return 0;
    const { squad, transferred } = selectPlayers(players.value, scope, selection.ids.value);
    return squad.length + transferred.length;
  }

  /**
   * @param {{ scope: string, format: string, title?: string, ids?: Iterable }} options
   *   title: freier Listentitel (PDF-Kopf, CSV-Dateiname); ids: Spieler-IDs für den Umfang "selection"
   *   (ohne Angabe: die aktuelle Markierung)
   * @returns {Promise<{ ok: true, filename: string } | { ok: false, reason: string }>}
   */
  async function run({ scope, format, title = '', ids = null }) {
    if (!canExportPlayerList(role.value)) return { ok: false, reason: 'forbidden' };
    if (!RUN_SCOPES.includes(scope) || !EXPORT_FORMATS.includes(format)) return { ok: false, reason: 'invalid' };
    /* Umfang für die Rolle nicht erlaubt (staff + Transferierte): Abbruch, BEVOR irgendetwas geladen wird —
       insbesondere keine Abfrage der Transferhistorie (listTransfers), auch nicht über einen manipulierten Aufruf */
    if (!exportScopesFor(role.value).includes(scope)) return { ok: false, reason: 'forbidden' };

    busy.value = true;
    error.value = null;
    try {
      const useIds = scope === SELECTION_SCOPE ? (ids ?? selection.ids.value) : null;
      const { squad, transferred } = selectPlayers(players.value, scope, useIds);
      if (squad.length + transferred.length === 0) {
        error.value = 'empty';
        return { ok: false, reason: 'empty' };
      }

      /* Transferhistorie der Transferierten. Schlägt eine Abfrage fehl, wird NICHT mit dem
         aktuellen Verein als Ersatz exportiert: eine stille falsche Angabe in einer Liste, die
         weitergegeben wird, ist schlimmer als ein Abbruch mit Meldung. */
      const transfers = new Map();
      try {
        await Promise.all(transferred.map(async (p) => transfers.set(p.id, await listTransfers(p.id))));
      } catch {
        error.value = 'transferFailed';
        return { ok: false, reason: 'transferFailed' };
      }

      const now = new Date();
      /* Vereinsnamen aus dem Klubregister (Rückfall auf den Text, falls das Register nicht geladen ist) */
      const table = buildExportTable(players.value, {
        scope, now: now.getTime(), transfers, clubsById: clubRegister.byId.value, ids: useIds,
      });
      const listTitle = sanitizeListTitle(title);
      const labels = exportLabels(t, { table, date: now, title: listTitle });
      const filename = exportFilename(scope, format, now, listTitle);

      if (format === 'csv') {
        const csv = buildCsv(table, { header: labels.header, group: labels.csvGroup });
        download(new Blob([csv], { type: 'text/csv;charset=utf-8' }), filename);
      } else {
        /* jsPDF erst jetzt laden: Es liegt in einem eigenen Chunk und belastet den Start der App nicht. */
        const [{ renderPlayerListPdf }, afh, crest] = await Promise.all([
          import('../export/playerListPdf.js'),
          loadImage('/afh-logo.png'),
          loadImage('/brand-mark.png'),
        ]);
        const doc = renderPlayerListPdf(table, labels, { afh, crest });
        download(doc.output('blob'), filename);
      }
      return { ok: true, filename };
    } catch (e) {
      console.error('[playerListExport] Export fehlgeschlagen:', e);
      error.value = 'failed';
      return { ok: false, reason: 'failed' };
    } finally {
      busy.value = false;
    }
  }

  return { allowed, scopes, busy, error, countFor, run };
}
